if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initSwatchHoverImage();
        this.initSizeTabs();
        this.clearInitialSizeSelection();
        this.initTitleWrapDetection();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      initTitleWrapDetection() {
        const titleEl = this.querySelector('.product-title.main-product-title');
        if (!titleEl) return;

        const checkWrap = () => {
          const lineHeight = parseFloat(getComputedStyle(titleEl).lineHeight);
          titleEl.classList.toggle('is-two-line', titleEl.scrollHeight > lineHeight * 1.5);
        };

        checkWrap();
        this.titleWrapResizeHandler = debounce(checkWrap, 150);
        window.addEventListener('resize', this.titleWrapResizeHandler);
      }

      clearInitialSizeSelection() {
        const isVariantAvailable = this.dataset.variantAvailable === 'true';
        const singleSizeFieldset = this.querySelector('.product-form__input--pill[data-single-size]');
        if (singleSizeFieldset) {
          const singleInput = singleSizeFieldset.querySelector('input[type="radio"]');
          if (singleInput) singleInput.checked = true;
          this.sizeSelected = true;
          const klarnaBlock = this.querySelector('.shopify-app-block:has(klarna-placement)');
          if (klarnaBlock) klarnaBlock.classList.toggle('klarna-hidden', !isVariantAvailable);
          return;
        }
        // No size picker at all (one-size / default-variant product)
        if (!this.querySelector('.product-form__input--pill input[type="radio"]')) {
          this.sizeSelected = true;
          const klarnaBlock = this.querySelector('.shopify-app-block:has(klarna-placement)');
          if (klarnaBlock) klarnaBlock.classList.toggle('klarna-hidden', !isVariantAvailable);
          return;
        }
        // Keep size checked if pre-selected via URL param or Shopify server-rendered checked state
        const hasPreSelectedVariant = new URLSearchParams(window.location.search).has('variant');
        const hasCheckedSize = !!this.querySelector('.product-form__input--pill input[type="radio"]:checked');
        if (hasPreSelectedVariant || hasCheckedSize) {
          this.sizeSelected = true;
          return;
        }
        this.querySelectorAll('.product-form__input--pill input[type="radio"]').forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });
        const klarnaBlock = this.querySelector('.shopify-app-block:has(klarna-placement)');
        if (klarnaBlock) klarnaBlock.classList.add('klarna-hidden');
      }

      initSizeTabs() {
        const sizeTabs = this.querySelectorAll('.size-tab');
        if (!sizeTabs.length) return;

        const sizeParts = this.querySelectorAll('.size-part');

        const updateVisibleSizes = (system) => {
          sizeParts.forEach((part) => {
            part.classList.toggle('is-visible', part.dataset.sizeSystem === system);
          });
          sizeTabs.forEach((tab) => {
            tab.classList.toggle('active', tab.dataset.sizeSystem === system);
          });
          document.dispatchEvent(
            new CustomEvent('product:size-system-change', {
              detail: { system },
            })
          );
        };

        sizeTabs.forEach((tab) => {
          tab.addEventListener('click', (e) => {
            e.preventDefault();
            // Store user's explicit choice for the duration of this page session,
            // scoped to the current country — a country switch invalidates it
            sessionStorage.setItem('preferred-size-system', tab.dataset.sizeSystem);
            sessionStorage.setItem('preferred-size-system-country', window.sizeConversionSettings?.currentCountry || '');
            updateVisibleSizes(tab.dataset.sizeSystem);
          });
        });

        // Get default system based on market
        const getDefaultSystem = () => {
          const settings = window.sizeConversionSettings;
          if (!settings) return 'eu';

          const country = settings.currentCountry;
          const ukMarkets = settings.uk.split(',').map(m => m.trim().toUpperCase());
          const usMarkets = settings.us.split(',').map(m => m.trim().toUpperCase());

          if (ukMarkets.includes(country)) return 'uk';
          if (usMarkets.includes(country)) return 'us';
          return 'eu';
        };

        // Honour the user's explicit tab choice only while they stay in the same
        // country — after a country switch, fall back to that market's default.
        const currentCountry = window.sizeConversionSettings?.currentCountry || '';
        let userChosen = sessionStorage.getItem('preferred-size-system');
        if (userChosen && sessionStorage.getItem('preferred-size-system-country') !== currentCountry) {
          sessionStorage.removeItem('preferred-size-system');
          sessionStorage.removeItem('preferred-size-system-country');
          userChosen = null;
        }
        const finalSystem = userChosen || getDefaultSystem();

        updateVisibleSizes(finalSystem);
      }

      initSwatchHoverImage() {
        const swatches = this.querySelectorAll('.swatch[data-image]');
        if (!swatches.length) return;

        const hoverContainer = document.querySelector('.swtach-hover-image');
        if (!hoverContainer) return;

        swatches.forEach((swatch) => {
          swatch.addEventListener('mouseenter', () => {
            if (window.innerWidth <= 1023) return;
            const imageUrl = swatch.dataset.image;
            if (!imageUrl) return;

            const mediaContainer =
              document.querySelector('.swiper-slide-active .product-media-container') ||
              document.querySelector('.product-media-container');
            if (mediaContainer) {
              const ratio = getComputedStyle(mediaContainer).getPropertyValue('--ratio').trim();
              if (ratio) hoverContainer.style.setProperty('--ratio', ratio);
            }

            hoverContainer.innerHTML = `<img src="${imageUrl}" alt="" loading="eager" style="width:100%;height:100%;object-fit:cover;">`;
            hoverContainer.classList.add('is-visible');
          });

          swatch.addEventListener('mouseleave', () => {
            if (window.innerWidth <= 1023) return;
            hoverContainer.innerHTML = '';
            hoverContainer.classList.remove('is-visible');
          });
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this.titleWrapResizeHandler) window.removeEventListener('resize', this.titleWrapResizeHandler);
      }

      initializeProductSwapUtility() {
        const isQuickAddModal = !!this.closest('quick-add-modal');

        this.preProcessHtmlCallbacks.push((html) => {
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));

          if (isQuickAddModal) {
            // Quick-add popups should not initialize Kiwi size charts at all.
            html.querySelectorAll('#KiwiSizingChart, [data-kiwi-size-chart]').forEach((chart) => chart.remove());
            this._kiwiSnapshot = null;
            return;
          }

          // Snapshot current Kiwi content before DOM swap so new node shows it immediately
          const oldChart = this.querySelector('#KiwiSizingChart, [data-kiwi-size-chart]');
          this._kiwiSnapshot = oldChart?.innerHTML || null;
        });
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          this.initSizeTabs();
          this.initSwatchHoverImage();

          if (isQuickAddModal) {
            newNode.querySelectorAll('#KiwiSizingChart, [data-kiwi-size-chart]').forEach((chart) => chart.remove());
            return;
          }

          // Inject snapshot into new chart node immediately — no blank flash during reinit
          if (this._kiwiSnapshot) {
            const newChart = newNode.querySelector('#KiwiSizingChart, [data-kiwi-size-chart]');
            if (newChart) newChart.innerHTML = this._kiwiSnapshot;
          }
          this._reinitKiwi(newNode);
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const isSizeChange = !!event.target.closest('.product-form__input--pill');

        if (isSizeChange) {
          this.sizeSelected = true;
        } else {
          // Color changed — require size re-selection
          this.sizeSelected = false;
        }

        // On color change, always strip size option values so Shopify doesn't auto-resolve a variant
        let effectiveOptionValues = selectedOptionValues;
        if (!isSizeChange) {
          const sizeOptionValueIds = new Set(
            Array.from(this.querySelectorAll('.product-form__input--pill input[type="radio"]'))
              .map((i) => i.dataset.optionValueId)
          );
          effectiveOptionValues = selectedOptionValues.filter((id) => !sizeOptionValueIds.has(id));
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, effectiveOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, isSizeChange || !!this.sizeSelected, isSizeChange),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        // Plain JS property, no DOM attribute: Shopify's wallet script mirrors
        // both disabled and aria-disabled from the add button onto the dynamic
        // checkout buttons (PayPal & co.), so any attribute set here greys them
        // for the duration of the variant fetch. The flag is checked in
        // product-form's onSubmitHandler instead.
        if (productForm) productForm.variantSyncPending = true;
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              // Render failed — don't leave the submit guard locked
              if (this.productForm) this.productForm.variantSyncPending = false;
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl, isSizeChange = false, isPillClick = false) {
        return (html) => {
          // Update landed — release the submit guard set in resetProductFormState
          if (this.productForm) this.productForm.variantSyncPending = false;

          const variant = this.getSelectedVariant(html);

          // One-size / no-size-option product: size is implicitly always selected —
          // never run the "reset size selection" path that hides CTA/Klarna/dynamic checkout
          const hasSizePicker = !!this.querySelector('.product-form__input--pill input[type="radio"]');
          const isSingleSize = !!this.querySelector('.product-form__input--pill[data-single-size]');
          if (!hasSizePicker || isSingleSize) isSizeChange = true;

          this.pickupAvailability?.update(variant);

          if (!isSizeChange) {
            // Strip checked state from size inputs in incoming HTML before DOM swap
            html.querySelectorAll('.product-form__input--pill input[type="radio"]').forEach((input) => {
              input.removeAttribute('checked');
              input.checked = false;
            });
          }

          // Pure size click: the picker's option states (color availability,
          // size cross-outs) only depend on the selected color, so replacing the
          // whole variant-selects DOM would change nothing visually — but it does
          // reset the size strip (scroll position, popup-moved fieldset, Kiwi
          // re-init), which reads as the sizes jumping around. Skip the swap.
          if (!isPillClick) this.updateOptionValues(html);

          if (!isSizeChange) {
            // Also uncheck size inputs in live DOM after swap (safety net)
            this.querySelectorAll('.product-form__input--pill input[type="radio"]').forEach((input) => {
              input.checked = false;
              input.removeAttribute('checked');
            });
          }

          if (isSizeChange && variant) {
            // Size explicitly selected — commit variant to URL and form
            this.updateURL(productUrl, variant.id);
            this.updateVariantInputs(variant.id);
          } else {
            // Color change or no size yet — clear variant from URL/form
            this.updateURL(productUrl, null);
            this.updateVariantInputs(null);
          }

          if (!variant || !isSizeChange) {
            if (!isSizeChange) {
              // Color changed — reset size selection state
              const submitBtn = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
              if (submitBtn) {
                submitBtn.classList.add('no-size__selected');
                submitBtn.removeAttribute('disabled');
                submitBtn.style.removeProperty('display');
              }

              const bisBtn = this.querySelector(`#BISTrigger-${this.dataset.section}`);
              if (bisBtn) {
                bisBtn.style.display = 'none';
                bisBtn.classList.add('no-size__selected');
                bisBtn.classList.remove('bis--hover-select-size', 'bis--size-prompt');
              }

              const submitBtnPeek = this.querySelector(`#ProductSubmitButtonPeek-${this.dataset.section}`);
              if (submitBtnPeek) {
                submitBtnPeek.removeAttribute('disabled');
                submitBtnPeek.style.removeProperty('display');
              }

              const bisBtnPeek = this.querySelector(`#BISTriggerPeek-${this.dataset.section}`);
              if (bisBtnPeek) {
                bisBtnPeek.style.display = 'none';
                bisBtnPeek.classList.add('no-size__selected');
                bisBtnPeek.classList.remove('bis--hover-select-size', 'bis--size-prompt');
              }

              const dynamicCheckout = this.querySelector(`#DynamicCheckout-${this.dataset.section}`);
              if (dynamicCheckout) dynamicCheckout.classList.add('hidden');

              const klarnaBlock = this.querySelector('.shopify-app-block:has(klarna-placement)');
              if (klarnaBlock) klarnaBlock.classList.add('klarna-hidden');

              // No variant because no size selected yet — not truly unavailable, don't show notify btn
              if (!variant) {
                publish(PUB_SUB_EVENTS.variantChange, {
                  data: { sectionId: this.sectionId, html, variant: null, noSizeSelected: true },
                });
                return;
              }
            } else if (!variant) {
              // Size was selected but still no variant — truly unavailable
              this.setUnavailable();
              return;
            }
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('PriceAtcPeek');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          if (isSizeChange) {
            const soldOutPeek = html.getElementById(`ProductSubmitButtonPeek-${this.sectionId}`)?.hasAttribute('disabled') ?? true;
            this.productForm?.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut
            );

            // Size selected — unlock CTA and show dynamic checkout
            const submitBtn = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
            if (submitBtn) submitBtn.classList.remove('no-size__selected', 'atc--hover-select-size');

            const bisBtn = this.querySelector(`#BISTrigger-${this.dataset.section}`);
            if (bisBtn) bisBtn.classList.remove('no-size__selected', 'bis--hover-select-size', 'bis--size-prompt');

            // Peek bar mirrors the same sold-out swap, since it's a separate button pair.
            // When sold out, leave its "Select size" state classes untouched — it's
            // being hidden in favor of Notify Me either way, and clearing them first
            // would flash the plain "Add to Cart" label for a frame before the
            // display:none below takes it out of the layout.
            const submitBtnPeek = this.querySelector(`#ProductSubmitButtonPeek-${this.dataset.section}`);
            if (submitBtnPeek) {
              submitBtnPeek.toggleAttribute('disabled', soldOutPeek);
              submitBtnPeek.style.display = soldOutPeek ? 'none' : '';
              if (!soldOutPeek) submitBtnPeek.classList.remove('no-size__selected', 'atc--hover-select-size');
            }

            const bisBtnPeek = this.querySelector(`#BISTriggerPeek-${this.dataset.section}`);
            if (bisBtnPeek) {
              bisBtnPeek.classList.remove('no-size__selected', 'bis--hover-select-size', 'bis--size-prompt');
              bisBtnPeek.style.display = soldOutPeek ? '' : 'none';
              const bisSource = html.getElementById(`BISTrigger-${this.sectionId}`);
              if (bisSource) {
                bisBtnPeek.dataset.variantId = bisSource.dataset.variantId;
                bisBtnPeek.dataset.variantName = bisSource.dataset.variantName;
              }
            }

            const dynamicCheckout = this.querySelector(`#DynamicCheckout-${this.dataset.section}`);
            if (dynamicCheckout) dynamicCheckout.classList.remove('hidden');

            const klarnaBlock = this.querySelector('.shopify-app-block:has(klarna-placement)');
            if (klarnaBlock) klarnaBlock.classList.toggle('klarna-hidden', !variant.available);

            // Hide size-select warning
            const warning = this.querySelector(`#SelectSizeWarning-${this.dataset.section}`);
            if (warning) warning.hidden = true;
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      _reinitKiwi(newNode) {
        const chart = newNode.querySelector('#KiwiSizingChart, [data-kiwi-size-chart]');
        if (!chart) return;

        clearInterval(this._kiwiPoll);
        clearTimeout(this._kiwiDebounce);
        this._kiwiPoll = null;

        if (this._kiwiStaging) {
          this._kiwiStaging._skip = true;
          this._kiwiStaging.remove();
          this._kiwiStaging = null;
        }

        this._kiwiDebounce = setTimeout(() => {
          const popup = document.querySelector('.size-popup-wrapper');
          const live =
            (popup && popup.querySelector('#KiwiSizingChart, [data-kiwi-size-chart]')) ||
            this.querySelector('#KiwiSizingChart, [data-kiwi-size-chart]') ||
            chart;

          if (!live.isConnected) return;
          if (!live.children.length) live.classList.add('kiwi-loading');

          const load = () => {
            const staging = Object.assign(document.createElement('div'), { _skip: false });
            staging.style.cssText = 'position:absolute;left:-9999px;visibility:hidden;pointer-events:none;';
            document.body.appendChild(staging);
            this._kiwiStaging = staging;

            const dm = Number(chart.dataset.displayMode);
            window.ks.loadSizing({
              productData: { product: chart.dataset.product, tags: chart.dataset.tags || '', type: chart.dataset.type || '', vendor: chart.dataset.vendor || '', collections: chart.dataset.collections || '' },
              options: { el: staging, ...(!isNaN(dm) && { displayMode: dm }), ...(chart.dataset.linkText && { buttonText: chart.dataset.linkText }) },
              loaded: () => {
                if (staging._skip) return;
                if (!live.isConnected) return;
                live.replaceChildren(...staging.childNodes);
                live.classList.remove('kiwi-loading');
                staging.remove();
                this._kiwiStaging = null;
                delete live.dataset.kiwiInitialized;
                document.dispatchEvent(new CustomEvent('kiwi:reloaded', { detail: { chart: live } }));
              },
            });
          };

          if (window.ks?.loadSizing) {
            load();
          } else {
            this._kiwiPoll = setInterval(() => { if (window.ks?.loadSizing) { clearInterval(this._kiwiPoll); this._kiwiPoll = null; load(); } }, 200);
            setTimeout(() => { clearInterval(this._kiwiPoll); this._kiwiPoll = null; }, 5000);
          }
        }, 0);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          false
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
